import { useCallback, useRef } from 'react'
import type { Workflow, PanOffset } from './types'
import { MINIMAP_WIDTH, MINIMAP_HEIGHT, ACTIVITY_COLORS } from './constants'
import { getWorkflowBounds, getConnectionPoints, getActivityWidth } from './geometry'

interface Props {
  workflow: Workflow
  zoomLevel: number
  panOffset: PanOffset
  canvasWidth: number
  canvasHeight: number
  onPanChange: (offset: PanOffset) => void
  theme: 'dark' | 'light'
}

export default function Minimap({
  workflow,
  zoomLevel,
  panOffset,
  canvasWidth,
  canvasHeight,
  onPanChange,
  theme,
}: Props) {
  const draggingRef = useRef(false)
  const lastRef = useRef({ x: 0, y: 0 })

  const bounds = getWorkflowBounds(workflow)
  if (!bounds) return null

  // Scale to fit within minimap
  const padding = 20
  const contentW = bounds.width + padding * 2
  const contentH = bounds.height + padding * 2
  const scaleX = MINIMAP_WIDTH / contentW
  const scaleY = MINIMAP_HEIGHT / contentH
  const scale = Math.min(scaleX, scaleY)

  // Viewport rectangle
  const viewX = (-panOffset.x / zoomLevel - bounds.x + padding) * scale
  const viewY = (-panOffset.y / zoomLevel - bounds.y + padding) * scale
  const viewW = (canvasWidth / zoomLevel) * scale
  const viewH = (canvasHeight / zoomLevel) * scale

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    draggingRef.current = true
    lastRef.current = { x: e.clientX, y: e.clientY }
    e.preventDefault()
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!draggingRef.current) return
    const dx = e.clientX - lastRef.current.x
    const dy = e.clientY - lastRef.current.y
    lastRef.current = { x: e.clientX, y: e.clientY }

    const canvasDx = (dx / scale) * zoomLevel
    const canvasDy = (dy / scale) * zoomLevel
    onPanChange({
      x: panOffset.x - canvasDx,
      y: panOffset.y - canvasDy,
    })
    e.preventDefault()
  }, [scale, zoomLevel, panOffset, onPanChange])

  const handleMouseUp = useCallback(() => {
    draggingRef.current = false
  }, [])

  const handleClick = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top

    // Convert minimap click to canvas coordinates
    const canvasX = mx / scale + bounds.x - padding
    const canvasY = my / scale + bounds.y - padding
    onPanChange({
      x: -canvasX * zoomLevel + canvasWidth / 2,
      y: -canvasY * zoomLevel + canvasHeight / 2,
    })
  }, [scale, bounds, zoomLevel, canvasWidth, canvasHeight, onPanChange])

  const lineColor = theme === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)'

  return (
    <svg
      width={MINIMAP_WIDTH}
      height={MINIMAP_HEIGHT}
      style={{
        position: 'absolute',
        bottom: 12,
        left: 12,
        background: theme === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(30,30,30,0.85)',
        border: '1px solid var(--color-border)',
        borderRadius: 4,
        cursor: 'grab',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleClick}
    >
      <g transform={`translate(${(-bounds.x + padding) * scale}, ${(-bounds.y + padding) * scale}) scale(${scale})`}>
        {/* Transitions */}
        {workflow.transitions.map(t => {
          const from = workflow.activities.find(a => a.id === t.from)
          const to = workflow.activities.find(a => a.id === t.to)
          if (!from || !to) return null
          const pts = getConnectionPoints(from, to)
          return (
            <line
              key={t.id}
              x1={pts.startX} y1={pts.startY}
              x2={pts.endX} y2={pts.endY}
              stroke={lineColor}
              strokeWidth={1 / scale}
            />
          )
        })}

        {/* Activities */}
        {workflow.activities.map(a => (
          <rect
            key={a.id}
            x={a.x} y={a.y}
            width={getActivityWidth(a)} height={a.height}
            fill={ACTIVITY_COLORS[a.type] || ACTIVITY_COLORS.activity}
            rx={2 / scale}
          />
        ))}
      </g>

      {/* Viewport rectangle */}
      <rect
        x={viewX} y={viewY}
        width={Math.max(viewW, 10)} height={Math.max(viewH, 10)}
        fill="none"
        stroke={theme === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)'}
        strokeWidth={1}
      />
    </svg>
  )
}
